import { toPng } from 'html-to-image';

/**
 * Generate a thumbnail PNG from a React Flow canvas element
 * @param canvasElement - The DOM element containing the React Flow canvas
 * @param options - Configuration options for thumbnail generation
 * @returns Base64 encoded PNG data URL
 */
export async function generateThumbnail(
  canvasElement: HTMLElement,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    backgroundColor?: string;
  } = {}
): Promise<string> {
  const {
    width = 1200,
    height = 630,
    quality = 0.9,
    backgroundColor = '#ffffff',
  } = options;

  try {
    // Generate the image with proper sizing and quality
    const dataUrl = await toPng(canvasElement, {
      quality,
      pixelRatio: 2, // Higher resolution for better quality
      width,
      height,
      backgroundColor,
      cacheBust: true,
      style: {
        transform: 'scale(1)',
        transformOrigin: 'top left',
      },
    });

    return dataUrl;
  } catch (error) {
    console.error('Error generating thumbnail:', error);
    throw new Error('Failed to generate thumbnail');
  }
}

/**
 * Convert base64 data URL to Blob
 * @param dataUrl - Base64 encoded data URL
 * @returns Blob object
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
}

/**
 * Get optimal thumbnail size based on number of tables
 * @param tableCount - Number of tables in the schema
 * @returns Width and height for thumbnail
 */
export function getOptimalThumbnailSize(tableCount: number): { width: number; height: number } {
  // Base size for social media (OpenGraph)
  const baseWidth = 1200;
  const baseHeight = 630;

  // Adjust based on table count for better visibility
  if (tableCount <= 3) {
    return { width: baseWidth, height: baseHeight };
  } else if (tableCount <= 6) {
    return { width: 1400, height: 800 };
  } else {
    return { width: 1600, height: 900 };
  }
}
